import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { logout } from "../../LoginPage/redux/auth.actions"
import {
  attachPaymentSuccess,
  changeNextCreateAccountStep,
  createBillingDescriptorSuccess,
  setDemoTourStep,
  userSetProp
} from "./user.actions"
import { USER_SLICE_NAME } from "./user.constants"
import {
  adyenUploadFiles,
  attachPayment,
  bulkUpdateAdminDescriptorStatus,
  changeBusinessAccount,
  changeEmailOnboarding,
  changeEmails,
  changeLanguage,
  changeOnboardingStep,
  changePassword,
  changeStatusEnrollment,
  checkMaverickDba,
  confirmEmails,
  connectMaverickTerminals,
  connectPaymentProcessor,
  createBillingDescriptor,
  createBusinessAccount,
  createNewPassword,
  declinePremiumFeatures,
  deleteBusinessAccount,
  deleteUserCard,
  demoDataToggle,
  disconnectPaymentProcessor,
  disconnectPaymentProcessorExternally,
  getAdminDescriptorsByEnrollment,
  getAdminEnrollments,
  getAutoRefundLogs,
  getBannerInfo,
  getBillingDescriptors,
  getBusinessAccount,
  getBusinessAccounts,
  getBusinessAccountsWithRole,
  getBusinessInfo,
  getDescriptorsByEnrollment,
  getEnrollments,
  getEnrollmentsWithoutDescriptors,
  getIntegrationLogs,
  getMaxCreateSteps,
  getMoreAdminDescriptorsByEnrollment,
  getMoreDescriptorsByEnrollment,
  getMoreVisaCodesByEnrollment,
  getPaymentProcessors,
  getPremiumFeatures,
  getUserInfo,
  getUserInfoByAdmin,
  getUsersCards,
  getVisaCodesByEnrollment,
  requestNewIntegration,
  sendCodePassword,
  sendOnboardingForm,
  setPassword,
  trackEvent,
  updateAdminDescriptorStatus,
  updateAdminDescriptorType,
  updateBillingDescriptorStatus,
  updateBusinessAccount,
  updateDefaultCard,
  updateEnrollment,
  updateUserPrice,
  userSave,
  userSaveAvatar,
  validateBillingDescriptor
} from "./user.thunks"

const emptyBillingDescriptors = () => ({
  descriptor_data: [],
  dont_remember_descriptors: false,
  dont_remember_arn_codes: false
})

const initialState = {
  fetching: false,
  errors: false,

  saveResult: null,
  saveFetching: false,
  businessAccountSwitching: false,

  businessAccountsWithRoleFetching: false,
  businessAccountsWithRole: [],

  businessAccountsFetching: false,
  businessAccounts: [],

  businessAccountFetching: false,
  businessAccount: null,
  autoRefundLogsFetching: false,
  autoRefundLogs: [],
  integrationLogsFetching: false,
  integrationLogs: [],

  isOneProcessorConnected: false,
  isManualRefundsConnected: false,
  paymentProcessors: [],
  enrollments: [],
  adminEnrollments: [],
  adminEnrollmentsFetching: false,
  userEnrollments: [],
  userEnrollmentsBusinessAccountId: null,
  userEnrollmentsFetching: false,
  userEnrollmentsError: null,
  descriptorsByEnrollment: null,
  descriptorsByEnrollmentFetching: false,
  moreDescriptorsByEnrollmentFetching: false,
  descriptorsByEnrollmentError: null,
  visaCodesByEnrollment: null,
  visaCodesByEnrollmentFetching: false,
  moreVisaCodesByEnrollmentFetching: false,
  visaCodesByEnrollmentError: null,
  adminDescriptorsByEnrollment: null,
  adminDescriptorsByEnrollmentFetching: false,
  moreAdminDescriptorsByEnrollmentFetching: false,
  adminDescriptorsByEnrollmentError: null,
  adminDescriptorStatusUpdating: false,
  adminDescriptorTypeUpdating: false,
  enrollmentsFetching: false,
  billingDescriptors: emptyBillingDescriptors(),
  usersCards: [],
  checkoutSession: null,
  errorDescriptors: [],
  errorBinCodes: [],
  errorCaidCodes: [],
  validateBillingDescriptorResult: null,

  nextCreateAccountStep: null,
  redirectAfterCreateAccount: null,
  nextManageEnrollmentStep: null,

  maxCreateStepsFetching: false,
  maxCreateSteps: 6,

  bannerInfo: null,
  bannerInfoFetching: true,

  businessInfo: null,

  premiumFeatures: [],

  saveAvatarFetching: false,
  verifyLastReqTime: null,
  demoTourStep: null,

  trackEventfetching: false,
  userInfoForAdmin: null,
  emailChanged: false,

  adyenFilesUploading: false,

  maverickDba: null,
  maverickDbaFetching: false,
  maverickDbaError: null,

  maverickConnect: null,
  maverickConnectFetching: false,
  maverickConnectError: null,

  demoDataShownToggleLoading: false
}

/** The legacy failure actions were dispatched as `{ errors }`; read that shape safely. */
const readErrors = (action) => (action.payload || {}).errors

const setSaveFetching = (state) => {
  state.saveFetching = true
}

const stopSaveFetching = (state) => {
  state.saveFetching = false
}

const stopSaveFetchingWithErrors = (state, action) => {
  state.saveFetching = false
  state.errors = readErrors(action)
}

const stopAllFetchingWithErrors = (state, action) => {
  state.fetching = false
  state.saveFetching = false
  state.errors = readErrors(action)
}

const setFetching = (state) => {
  state.fetching = true
}

const stopFetching = (state) => {
  state.fetching = false
}

const applyDescriptorErrors = (state, errors) => {
  state.errorDescriptors = errors && errors?.descriptors ? [...errors.descriptors] : []
  state.errorBinCodes = errors && errors?.bin_codes ? [...errors.bin_codes] : []
  state.errorCaidCodes = errors && errors?.caid_codes ? [...errors.caid_codes] : []
}

const applyAttachPaymentSuccess = (state) => {
  state.fetching = false
  state.checkoutSession = null
  state.nextCreateAccountStep = 8
  state.redirectAfterCreateAccount = null
  state.businessAccount = null
  // saveResult: 'business_created'
  state.saveResult = null
}

const applyAdminDescriptorPatch = (state, payload, field, key) => {
  const updatedId = payload?.billing_descriptor_id
  if (!state.adminDescriptorsByEnrollment) return

  state.adminDescriptorsByEnrollment.results = (
    state.adminDescriptorsByEnrollment.results || []
  ).map((item) =>
    item?.billing_descriptor_id === updatedId
      ? { ...item, [field]: payload?.[key] ?? item?.[field] }
      : item
  )
}

const slice = createSlice({
  name: USER_SLICE_NAME,
  initialState,
  reducers: {
    setVerifyLastReqTime(state, action) {
      state.verifyLastReqTime = action.payload
    },
    userSaveResultReset(state) {
      state.saveFetching = false
      state.saveResult = null
    },
    userErrorsReset(state) {
      state.errors = false
    },
    validateBillingDescriptorReset(state) {
      state.validateBillingDescriptorResult = null
      state.errorDescriptors = []
      state.errorBinCodes = []
      state.errorCaidCodes = []
    },
    checkMaverickDbaReset(state) {
      state.maverickDba = null
      state.maverickDbaError = null
      state.maverickDbaFetching = false
    },
    connectMaverickTerminalsReset(state) {
      state.maverickConnect = null
      state.maverickConnectError = null
      state.maverickConnectFetching = false
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- plain actions shared with the thunks ---------------- */
      .addCase(userSetProp, (state, action) => {
        const { key, value } = action.payload
        state[key] = value
        if (key === "nextManageEnrollmentStep" && value === null) {
          state.billingDescriptors = emptyBillingDescriptors()
        }
      })
      .addCase(changeNextCreateAccountStep, (state, action) => {
        const nextCreateAccountStep = action.payload
        state.nextCreateAccountStep = nextCreateAccountStep
        if (!nextCreateAccountStep || nextCreateAccountStep === 1) {
          state.businessAccount = null
          state.paymentProcessors = []
          state.enrollments = []
          state.billingDescriptors = emptyBillingDescriptors()
          state.checkoutSession = null
        }
      })
      .addCase(setDemoTourStep, (state, action) => {
        state.demoTourStep = action.payload
      })
      .addCase(logout, () => initialState)

      /* ---------------- profile ---------------- */
      .addCase(getUserInfo.pending, setFetching)
      .addCase(getUserInfo.fulfilled, stopFetching)
      .addCase(getUserInfo.rejected, (state, action) => {
        state.fetching = false
        state.errors = action.payload
      })
      .addCase(getUserInfoByAdmin.pending, setFetching)
      .addCase(getUserInfoByAdmin.fulfilled, (state, action) => {
        state.fetching = false
        state.userInfoForAdmin = action.payload
      })
      .addCase(getUserInfoByAdmin.rejected, (state, action) => {
        state.fetching = false
        state.errors = action.payload
      })
      .addCase(userSave.pending, setSaveFetching)
      .addCase(userSave.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = true
        if (action.payload?.user_id) state.userInfoForAdmin = action.payload?.data
      })
      .addCase(userSave.rejected, (state, action) => {
        state.saveAvatarFetching = false
        state.errors = { userPersonalError: action.payload }
        state.saveFetching = false
      })
      .addCase(userSaveAvatar.pending, (state) => {
        state.saveAvatarFetching = true
      })
      .addCase(userSaveAvatar.fulfilled, (state, action) => {
        state.saveAvatarFetching = false
        state.saveResult = action.payload?.avatar ? { avatar: "removed" } : { avatar: "delete" }
      })
      .addCase(userSaveAvatar.rejected, (state, action) => {
        state.saveAvatarFetching = false
        state.errors = { avatarError: readErrors(action) }
      })
      .addCase(trackEvent.pending, (state) => {
        state.trackEventfetching = true
      })
      .addCase(trackEvent.fulfilled, (state) => {
        state.trackEventfetching = false
      })
      .addCase(trackEvent.rejected, (state) => {
        state.trackEventfetching = false
      })

      /* ---------------- password ---------------- */
      .addCase(changePassword.pending, setSaveFetching)
      .addCase(changePassword.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { create: true }
      })
      .addCase(changePassword.rejected, stopSaveFetchingWithErrors)
      .addCase(sendCodePassword.pending, setSaveFetching)
      .addCase(sendCodePassword.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = true
      })
      .addCase(sendCodePassword.rejected, stopAllFetchingWithErrors)
      .addCase(createNewPassword.pending, setSaveFetching)
      .addCase(createNewPassword.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { create: true }
      })
      .addCase(createNewPassword.rejected, stopAllFetchingWithErrors)
      .addCase(setPassword.pending, setSaveFetching)
      .addCase(setPassword.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { setPassword: true }
      })
      .addCase(setPassword.rejected, stopAllFetchingWithErrors)

      /* ---------------- email & language ---------------- */
      .addCase(changeEmails.pending, setSaveFetching)
      .addCase(changeEmails.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { confirm: true }
      })
      .addCase(changeEmails.rejected, stopSaveFetchingWithErrors)
      .addCase(confirmEmails.pending, setSaveFetching)
      .addCase(confirmEmails.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { confirmEmail: true }
      })
      .addCase(confirmEmails.rejected, (state, action) => {
        state.saveFetching = false
        state.errors = { confirmError: readErrors(action) }
      })
      .addCase(changeLanguage.pending, setSaveFetching)
      .addCase(changeLanguage.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { changeLanguage: true }
      })
      .addCase(changeLanguage.rejected, stopSaveFetchingWithErrors)
      .addCase(changeEmailOnboarding.pending, (state) => {
        state.saveFetching = true
        state.emailChanged = false
        state.errors = false
      })
      .addCase(changeEmailOnboarding.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = { confirm: true }
        state.emailChanged = true
        state.errors = false
      })
      .addCase(changeEmailOnboarding.rejected, (state, action) => {
        state.saveFetching = false
        state.errors = readErrors(action)
        state.emailChanged = false
      })

      /* ---------------- onboarding ---------------- */
      .addCase(sendOnboardingForm.pending, setSaveFetching)
      .addCase(sendOnboardingForm.fulfilled, stopSaveFetching)
      .addCase(sendOnboardingForm.rejected, stopSaveFetching)
      .addCase(changeOnboardingStep.pending, setFetching)
      .addCase(changeOnboardingStep.fulfilled, stopFetching)
      .addCase(changeOnboardingStep.rejected, stopFetching)
      .addCase(getMaxCreateSteps.pending, (state) => {
        state.maxCreateStepsFetching = true
      })
      .addCase(getMaxCreateSteps.fulfilled, (state, action) => {
        state.maxCreateStepsFetching = false
        state.maxCreateSteps = action.payload?.["onboarding_steps"]
      })
      .addCase(getMaxCreateSteps.rejected, (state) => {
        state.maxCreateStepsFetching = false
      })

      /* ---------------- business accounts ---------------- */
      .addCase(createBusinessAccount.pending, (state) => {
        state.businessAccountsFetching = true
      })
      .addCase(createBusinessAccount.fulfilled, (state, action) => {
        const businessAccount = action.payload
        state.businessAccountsFetching = false
        if (!state.businessAccounts.length) state.businessAccounts = [businessAccount]
        state.businessAccount = businessAccount
      })
      .addCase(createBusinessAccount.rejected, (state, action) => {
        state.businessAccountsFetching = false
        state.errors = readErrors(action)
      })
      .addCase(updateBusinessAccount.pending, setSaveFetching)
      .addCase(updateBusinessAccount.fulfilled, (state, action) => {
        const businessAccount = action.payload
        state.saveFetching = false
        state.saveResult = true

        if (businessAccount?.settings_for_auto_refund || businessAccount?.name) {
          state.businessAccount = { ...state.businessAccount, ...businessAccount }
          state.businessAccounts = state.businessAccounts.map((ba) =>
            ba.id === businessAccount.id ? { ...ba, ...businessAccount } : ba
          )
        } else {
          state.businessAccount = {
            ...state.businessAccount,
            settings_for_auto_refund: { ...businessAccount }
          }
        }
      })
      .addCase(updateBusinessAccount.rejected, (state, action) => {
        state.saveAvatarFetching = false
        state.saveFetching = false
        state.errors = { businessAccountError: action.payload }
      })
      .addCase(getBusinessAccount.pending, (state) => {
        state.businessAccountFetching = true
        state.businessAccount = null
      })
      .addCase(getBusinessAccount.fulfilled, (state, action) => {
        state.businessAccountFetching = false
        state.businessAccount = action.payload
      })
      .addCase(getBusinessAccount.rejected, (state) => {
        state.businessAccountFetching = false
      })
      .addCase(getBusinessAccounts.pending, (state) => {
        state.businessAccountsFetching = true
        state.businessAccounts = []
      })
      .addCase(getBusinessAccounts.fulfilled, (state, action) => {
        state.businessAccountsFetching = false
        state.businessAccounts = [...action.payload.results]
      })
      .addCase(getBusinessAccounts.rejected, (state) => {
        state.businessAccountsFetching = false
      })
      .addCase(getBusinessAccountsWithRole.pending, (state) => {
        state.businessAccountsWithRoleFetching = true
      })
      .addCase(getBusinessAccountsWithRole.fulfilled, (state, action) => {
        state.businessAccountsWithRoleFetching = false
        state.businessAccountsWithRole = action.payload
      })
      .addCase(getBusinessAccountsWithRole.rejected, (state) => {
        state.businessAccountsWithRoleFetching = false
      })
      .addCase(deleteBusinessAccount.pending, (state) => {
        state.businessAccountFetching = true
      })
      .addCase(deleteBusinessAccount.fulfilled, (state) => {
        state.businessAccountFetching = false
        state.nextCreateAccountStep = null
        state.businessAccount = null
        state.autoRefundLogs = []
        state.paymentProcessors = []
        state.enrollments = []
        state.billingDescriptors = emptyBillingDescriptors()
        state.checkoutSession = null
      })
      .addCase(deleteBusinessAccount.rejected, (state) => {
        state.businessAccountFetching = false
      })
      .addCase(changeBusinessAccount.pending, (state) => {
        state.businessAccountSwitching = true
        state.businessAccount = null
        state.userEnrollmentsBusinessAccountId = null
        state.descriptorsByEnrollment = null
        state.checkoutSession = null
        // enrollments belong to the previous account; keep them out of the next one
        state.enrollments = []
        state.billingDescriptors = emptyBillingDescriptors()
      })
      .addCase(changeBusinessAccount.fulfilled, (state) => {
        state.businessAccountSwitching = false
      })
      .addCase(changeBusinessAccount.rejected, (state) => {
        state.businessAccountSwitching = false
      })
      .addCase(getBusinessInfo.fulfilled, (state, action) => {
        state.businessInfo = action.payload
      })
      .addCase(getBannerInfo.pending, (state) => {
        state.bannerInfoFetching = true
      })
      .addCase(getBannerInfo.fulfilled, (state, action) => {
        state.bannerInfoFetching = false
        state.bannerInfo = action.payload
      })
      .addCase(getBannerInfo.rejected, (state) => {
        state.bannerInfoFetching = false
      })
      .addCase(updateUserPrice.pending, setSaveFetching)
      .addCase(updateUserPrice.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = "update_price"
        state.businessAccount = { ...state.businessAccount, ...action.payload }
      })
      .addCase(updateUserPrice.rejected, stopSaveFetching)
      .addCase(getAutoRefundLogs.pending, (state) => {
        state.autoRefundLogsFetching = true
      })
      .addCase(getAutoRefundLogs.fulfilled, (state, action) => {
        state.autoRefundLogsFetching = false
        state.autoRefundLogs = action.payload?.results || action.payload || []
      })
      .addCase(getAutoRefundLogs.rejected, (state) => {
        state.autoRefundLogsFetching = false
      })
      .addCase(getIntegrationLogs.pending, (state) => {
        state.integrationLogsFetching = true
      })
      .addCase(getIntegrationLogs.fulfilled, (state, action) => {
        state.integrationLogsFetching = false
        state.integrationLogs = action.payload?.results || action.payload || []
      })
      .addCase(getIntegrationLogs.rejected, (state) => {
        state.integrationLogsFetching = false
      })

      /* ---------------- payment processors ---------------- */
      .addCase(getPaymentProcessors.pending, setFetching)
      .addCase(getPaymentProcessors.fulfilled, (state, action) => {
        const results = action.payload?.results
        state.fetching = false
        state.paymentProcessors = [...results]
        state.isOneProcessorConnected = results.some((el) => el.is_connected === true)
        state.isManualRefundsConnected = results?.find(
          (el) => el.service_name === "manual_refunds"
        )?.["is_connected"]
      })
      .addCase(getPaymentProcessors.rejected, stopFetching)
      .addCase(connectPaymentProcessor.pending, (state) => {
        state.businessAccountFetching = true
        state.saveFetching = true
      })
      .addCase(connectPaymentProcessor.fulfilled, (state, action) => {
        const paymentProcessor = action.payload
        const newPaymentProcessors = state.paymentProcessors.map((el) => ({
          ...el,
          is_connected:
            (el.service_name === "manual_refunds" ? false : el?.is_connected) ||
            el?.id === paymentProcessor?.id
        }))

        state.businessAccountFetching = false
        state.isOneProcessorConnected = true
        state.isManualRefundsConnected = newPaymentProcessors?.find(
          (el) => el.service_name === "manual_refunds"
        )?.["is_connected"]
        state.paymentProcessors = newPaymentProcessors
        state.saveResult = "connected"
        state.saveFetching = false
      })
      .addCase(connectPaymentProcessor.rejected, (state, action) => {
        state.businessAccountFetching = false
        state.saveFetching = false
        state.saveResult = { connect_error: action.payload }
      })
      .addCase(disconnectPaymentProcessor.pending, (state) => {
        state.businessAccountFetching = true
        state.saveFetching = true
      })
      .addCase(disconnectPaymentProcessor.fulfilled, (state, action) => {
        const paymentProcessor = action.payload
        const newPaymentProcessors = state.paymentProcessors
          ?.filter(
            ({ id }) =>
              !(
                ["slack", "premium_support", "full"].includes(paymentProcessor?.id) &&
                id === paymentProcessor?.id
              )
          )
          ?.map((el) => ({
            ...el,
            is_connected: el?.id === paymentProcessor?.id ? false : el?.is_connected,
            connected_account: el?.id === paymentProcessor?.id ? null : el?.connected_account
          }))

        state.businessAccountFetching = false
        state.isOneProcessorConnected = newPaymentProcessors.some((el) => el.is_connected)
        state.isManualRefundsConnected = newPaymentProcessors?.find(
          (el) => el.service_name === "manual_refunds"
        )?.["is_connected"]
        state.paymentProcessors = newPaymentProcessors
        state.saveResult = "disconnected"
        state.saveFetching = false
      })
      .addCase(disconnectPaymentProcessor.rejected, (state) => {
        state.businessAccountFetching = false
        state.saveFetching = false
      })
      .addCase(disconnectPaymentProcessorExternally.pending, (state) => {
        state.businessAccountFetching = true
        state.saveFetching = true
      })
      .addCase(disconnectPaymentProcessorExternally.fulfilled, (state) => {
        state.businessAccountFetching = false
        state.saveFetching = false
        state.saveResult = "disconnected"
      })
      .addCase(disconnectPaymentProcessorExternally.rejected, (state) => {
        state.businessAccountFetching = false
        state.saveFetching = false
      })
      .addCase(adyenUploadFiles.pending, (state) => {
        state.adyenFilesUploading = true
      })
      .addCase(adyenUploadFiles.fulfilled, (state) => {
        state.adyenFilesUploading = false
      })
      .addCase(adyenUploadFiles.rejected, (state) => {
        state.adyenFilesUploading = false
      })
      .addCase(checkMaverickDba.pending, (state) => {
        state.maverickDbaFetching = true
        state.maverickDbaError = null
      })
      .addCase(checkMaverickDba.fulfilled, (state, action) => {
        state.maverickDbaFetching = false
        state.maverickDba = action.payload
      })
      .addCase(checkMaverickDba.rejected, (state, action) => {
        state.maverickDbaFetching = false
        state.maverickDbaError = readErrors(action)
      })
      .addCase(connectMaverickTerminals.pending, (state) => {
        state.maverickConnectFetching = true
        state.maverickConnectError = null
      })
      .addCase(connectMaverickTerminals.fulfilled, (state, action) => {
        state.maverickConnectFetching = false
        state.maverickConnect = action.payload
        state.saveResult = "connected"
      })
      .addCase(connectMaverickTerminals.rejected, (state, action) => {
        state.maverickConnectFetching = false
        state.maverickConnectError = readErrors(action)
      })
      .addCase(requestNewIntegration.pending, setSaveFetching)
      .addCase(requestNewIntegration.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "request_new_integration"
      })
      .addCase(requestNewIntegration.rejected, stopSaveFetching)

      /* ---------------- billing descriptors ---------------- */
      .addCase(createBillingDescriptor.pending, setFetching)
      .addCase(createBillingDescriptorSuccess, (state, action) => {
        state.fetching = false
        state.billingDescriptors = action.payload
        state.nextCreateAccountStep = state.nextCreateAccountStep
          ? state.nextCreateAccountStep === 4
            ? 5
            : state.nextCreateAccountStep
          : null
        state.errorDescriptors = []
        state.errorBinCodes = []
        state.errorCaidCodes = []
      })
      .addCase(createBillingDescriptor.rejected, (state, action) => {
        state.fetching = false
        applyDescriptorErrors(state, action.payload)
      })
      .addCase(validateBillingDescriptor.pending, (state) => {
        state.fetching = true
        state.validateBillingDescriptorResult = null
      })
      .addCase(validateBillingDescriptor.fulfilled, (state, action) => {
        state.fetching = false
        state.validateBillingDescriptorResult = action.payload
        state.errorDescriptors = []
        state.errorBinCodes = []
        state.errorCaidCodes = []
      })
      .addCase(validateBillingDescriptor.rejected, (state, action) => {
        state.fetching = false
        state.validateBillingDescriptorResult = null
        applyDescriptorErrors(state, action.payload)
      })
      .addCase(updateBillingDescriptorStatus.pending, setSaveFetching)
      .addCase(updateBillingDescriptorStatus.fulfilled, (state) => {
        state.saveFetching = false
        state.saveResult = "descriptor_status_changed"
      })
      .addCase(updateBillingDescriptorStatus.rejected, stopSaveFetching)
      .addCase(getBillingDescriptors.pending, setFetching)
      .addCase(getBillingDescriptors.fulfilled, (state, action) => {
        state.fetching = false
        state.billingDescriptors = action.payload
      })
      .addCase(getBillingDescriptors.rejected, stopFetching)
      .addCase(getDescriptorsByEnrollment.pending, (state) => {
        state.descriptorsByEnrollmentFetching = true
      })
      .addCase(getDescriptorsByEnrollment.fulfilled, (state, action) => {
        state.descriptorsByEnrollmentFetching = false
        state.descriptorsByEnrollment = action.payload
      })
      .addCase(getDescriptorsByEnrollment.rejected, (state) => {
        state.descriptorsByEnrollmentFetching = false
      })
      .addCase(getMoreDescriptorsByEnrollment.pending, (state) => {
        state.moreDescriptorsByEnrollmentFetching = true
      })
      .addCase(getMoreDescriptorsByEnrollment.fulfilled, (state, action) => {
        state.moreDescriptorsByEnrollmentFetching = false
        state.descriptorsByEnrollment = {
          ...action.payload,
          results: [...state.descriptorsByEnrollment.results, ...(action.payload?.results || [])]
        }
      })
      .addCase(getMoreDescriptorsByEnrollment.rejected, (state) => {
        state.moreDescriptorsByEnrollmentFetching = false
      })
      .addCase(getVisaCodesByEnrollment.pending, (state) => {
        state.visaCodesByEnrollmentFetching = true
      })
      .addCase(getVisaCodesByEnrollment.fulfilled, (state, action) => {
        state.visaCodesByEnrollmentFetching = false
        state.visaCodesByEnrollment = action.payload
      })
      .addCase(getVisaCodesByEnrollment.rejected, (state) => {
        state.visaCodesByEnrollmentFetching = false
      })
      .addCase(getMoreVisaCodesByEnrollment.pending, (state) => {
        state.moreVisaCodesByEnrollmentFetching = true
      })
      .addCase(getMoreVisaCodesByEnrollment.fulfilled, (state, action) => {
        state.moreVisaCodesByEnrollmentFetching = false
        state.visaCodesByEnrollment = {
          ...action.payload,
          results: [
            ...(state.visaCodesByEnrollment?.results || []),
            ...(action.payload?.results || [])
          ]
        }
      })
      .addCase(getMoreVisaCodesByEnrollment.rejected, (state) => {
        state.moreVisaCodesByEnrollmentFetching = false
      })
      .addCase(getAdminDescriptorsByEnrollment.pending, (state) => {
        state.adminDescriptorsByEnrollmentFetching = true
      })
      .addCase(getAdminDescriptorsByEnrollment.fulfilled, (state, action) => {
        state.adminDescriptorsByEnrollmentFetching = false
        state.adminDescriptorsByEnrollment = action.payload
      })
      .addCase(getAdminDescriptorsByEnrollment.rejected, (state) => {
        state.adminDescriptorsByEnrollmentFetching = false
      })
      .addCase(getMoreAdminDescriptorsByEnrollment.pending, (state) => {
        state.moreAdminDescriptorsByEnrollmentFetching = true
      })
      .addCase(getMoreAdminDescriptorsByEnrollment.fulfilled, (state, action) => {
        state.moreAdminDescriptorsByEnrollmentFetching = false
        state.adminDescriptorsByEnrollment = {
          ...action.payload,
          results: [
            ...(state.adminDescriptorsByEnrollment?.results || []),
            ...(action.payload?.results || [])
          ]
        }
      })
      .addCase(getMoreAdminDescriptorsByEnrollment.rejected, (state) => {
        state.moreAdminDescriptorsByEnrollmentFetching = false
      })
      .addCase(updateAdminDescriptorStatus.pending, (state) => {
        state.adminDescriptorStatusUpdating = true
      })
      .addCase(updateAdminDescriptorStatus.fulfilled, (state, action) => {
        state.adminDescriptorStatusUpdating = false
        applyAdminDescriptorPatch(state, action.payload, "current_status", "status")
      })
      .addCase(updateAdminDescriptorStatus.rejected, (state) => {
        state.adminDescriptorStatusUpdating = false
      })
      .addCase(updateAdminDescriptorType.pending, (state) => {
        state.adminDescriptorTypeUpdating = true
      })
      .addCase(updateAdminDescriptorType.fulfilled, (state, action) => {
        state.adminDescriptorTypeUpdating = false
        applyAdminDescriptorPatch(state, action.payload, "descriptor_type", "descriptor_type")
      })
      .addCase(updateAdminDescriptorType.rejected, (state) => {
        state.adminDescriptorTypeUpdating = false
      })
      .addCase(bulkUpdateAdminDescriptorStatus.fulfilled, (state, action) => {
        const updated = Array.isArray(action.payload) ? action.payload : []
        if (!updated.length || !state.adminDescriptorsByEnrollment) return

        const updatedById = {}
        updated.forEach((item) => {
          updatedById[item?.id] = item
        })

        state.adminDescriptorsByEnrollment.results = (
          state.adminDescriptorsByEnrollment.results || []
        ).map((item) => {
          const patch = updatedById[item?.billing_descriptor_id]
          if (!patch) return item

          return {
            ...item,
            current_status: patch.current_status ?? item.current_status,
            descriptor_type: patch.match_type ?? item.descriptor_type
          }
        })
      })

      /* ---------------- enrollments ---------------- */
      .addCase(getEnrollments.pending, (state) => {
        state.fetching = true
        state.enrollmentsFetching = true
      })
      .addCase(getEnrollments.fulfilled, (state, action) => {
        state.fetching = false
        state.enrollmentsFetching = false
        state.enrollments = [...action.payload.results]
      })
      .addCase(getEnrollments.rejected, (state) => {
        state.fetching = false
        state.enrollmentsFetching = false
      })
      .addCase(getAdminEnrollments.pending, (state) => {
        state.adminEnrollmentsFetching = true
        state.adminEnrollments = []
      })
      .addCase(getAdminEnrollments.fulfilled, (state, action) => {
        const results = action.payload?.results || action.payload || []
        state.adminEnrollmentsFetching = false
        state.adminEnrollments = Array.isArray(results) ? results : []
      })
      .addCase(getAdminEnrollments.rejected, (state) => {
        state.adminEnrollmentsFetching = false
      })
      .addCase(getEnrollmentsWithoutDescriptors.pending, (state, action) => {
        state.userEnrollmentsFetching = true
        state.userEnrollmentsBusinessAccountId = action.meta.arg
        state.userEnrollments = []
        state.descriptorsByEnrollment = null
        state.visaCodesByEnrollment = null
      })
      .addCase(getEnrollmentsWithoutDescriptors.fulfilled, (state, action) => {
        state.userEnrollmentsFetching = false
        state.userEnrollments = action.payload
      })
      .addCase(getEnrollmentsWithoutDescriptors.rejected, (state) => {
        state.userEnrollmentsFetching = false
      })
      .addCase(updateEnrollment.pending, setSaveFetching)
      .addCase(updateEnrollment.fulfilled, (state, action) => {
        const enrollment = action.payload
        state.saveFetching = false
        state.saveResult = enrollment?.["is_active"]
          ? { enrollment_turn_on: enrollment?.name || true }
          : { enrollment_turn_off: enrollment?.name || true }
        state.enrollments = state.enrollments.map((el) =>
          el?.id === enrollment?.id ? enrollment : el
        )
      })
      .addCase(updateEnrollment.rejected, stopSaveFetching)
      .addCase(changeStatusEnrollment.pending, setSaveFetching)
      .addCase(changeStatusEnrollment.fulfilled, (state, action) => {
        const enrollment = action.payload
        state.saveFetching = false
        state.saveResult = "change_status"
        state.enrollments = state.enrollments?.map((el) =>
          el?.id === enrollment?.id ? { ...enrollment } : el
        )
      })
      .addCase(changeStatusEnrollment.rejected, stopSaveFetching)

      /* ---------------- cards & payments ---------------- */
      .addCase(getUsersCards.pending, setFetching)
      .addCase(getUsersCards.fulfilled, (state, action) => {
        state.fetching = false
        state.usersCards = action.payload.results
      })
      .addCase(getUsersCards.rejected, stopFetching)
      .addCase(deleteUserCard.pending, setFetching)
      .addCase(deleteUserCard.fulfilled, (state, action) => {
        state.fetching = false
        state.usersCards = state.usersCards.filter((card) => card.id !== action.payload?.card_id)
      })
      .addCase(deleteUserCard.rejected, stopFetching)
      .addCase(updateDefaultCard.pending, setFetching)
      .addCase(updateDefaultCard.fulfilled, (state, action) => {
        const { id } = action.payload
        const oldCard = state.usersCards.find((card) => card.is_default)
        state.fetching = false
        state.usersCards = state.usersCards.map((card) => {
          if (card.id === oldCard?.id) return { ...card, is_default: false }
          if (card.id === id) return { ...card, is_default: true }
          return card
        })
      })
      .addCase(updateDefaultCard.rejected, stopFetching)
      .addCase(attachPayment.pending, setFetching)
      .addCase(attachPayment.fulfilled, applyAttachPaymentSuccess)
      // also fired standalone by `createBillingDescriptor` when onboarding finishes early
      .addCase(attachPaymentSuccess, applyAttachPaymentSuccess)
      .addCase(attachPayment.rejected, stopFetching)

      /* ---------------- premium features & demo data ---------------- */
      .addCase(getPremiumFeatures.pending, setFetching)
      .addCase(getPremiumFeatures.fulfilled, (state, action) => {
        state.fetching = false
        state.premiumFeatures = action.payload
      })
      .addCase(getPremiumFeatures.rejected, stopFetching)
      .addCase(declinePremiumFeatures.pending, setSaveFetching)
      .addCase(declinePremiumFeatures.fulfilled, stopSaveFetching)
      .addCase(declinePremiumFeatures.rejected, stopSaveFetching)
      .addCase(demoDataToggle.pending, (state) => {
        state.demoDataShownToggleLoading = true
      })
      .addCase(demoDataToggle.fulfilled, (state) => {
        state.demoDataShownToggleLoading = false
      })
      .addCase(demoDataToggle.rejected, (state) => {
        state.demoDataShownToggleLoading = false
      })
  }
})

export const userActions = slice.actions

export const UserSelectors = {
  getUser: (state) => state[USER_SLICE_NAME],
  getNextCreateAccountStep: (state) => state[USER_SLICE_NAME].nextCreateAccountStep,
  getNextManageEnrollmentStep: (state) => state[USER_SLICE_NAME].nextManageEnrollmentStep,
  getRedirectAfterCreateAccount: (state) => state[USER_SLICE_NAME].redirectAfterCreateAccount,
  getEnrollmentInfo: (state, ...args) =>
    state[USER_SLICE_NAME].enrollments?.find((el) => el.id === args[0]),
  getMaxCreateSteps: (state) => state[USER_SLICE_NAME].maxCreateSteps
}

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: "userThunk",
    storage,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
